import { css } from '@emotion/react';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { ToggleComponent } from '~/_shared/baseComponents/toggle';
import {
  createColor, createColorWithOpacity, guaranteeHash,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import { BoundaryTerritoryListingLimit } from '~/boundary/constants';
import { useLoadMoreListing } from '~/boundary/hooks/useLoadMoreListing';
import { BoundarySettingsLoadMoreRowComponent } from '~/boundary/settings/table/row/BoundarySettingsLoadMoreRow.component';
import { isBoundaryTerritoryEmpty } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import type { BoundaryTerritoryId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryTerritoryAssignments } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  type BoundaryTerritory, type BoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { translateBoundaryTerritoryDisplayName } from '../../../../../boundary/settings/boundarySettings.helpers';
import { BoundarySettingsCustomTerritoryDataComponent } from '../../../../../boundary/settings/custom/boundarySettingsCustomTerritoryDataComponent';
import { BoundarySettingsRestoreButtonComponent } from '../../../../../boundary/settings/restoreButton/boundarySettingsRestoreButton.component';
import { BoundarySettingsTableComponent } from '../../../../../boundary/settings/table/boundarySettingsTable.component';
import { BoundarySettingsTableRowComponent } from '../../../../../boundary/settings/table/row/boundarySettingsTableRow.component';

const toggleLabelStyle = (theme: Theme) => css({
  fontSize: 12,
  textTransform: 'uppercase',
  color: theme.textColors.secondary,
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 4,
});

const boundarySettingsActionsWrapperStyle = css({
  padding: '20px 24px 20px',
  display: 'flex',
  justifyContent: 'space-between',
});

const addTopsideDelimiterStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.borderColors.primary}`,
});

const restoreColorsButtonStyle = css({
  padding: 0,
});

type BoundarySettingsGroupWithoutCommonProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  setIsFormValid: (isFormValid: boolean) => void;
  isValid: boolean;
  restoreDefaultColors: () => void;
}>;

export const BoundaryFillSettingsGroupComponent: FC<BoundarySettingsGroupWithoutCommonProps> = (props) => {
  const [t] = useTranslation();
  const [showEmptyBoundaries, setShowEmptyBoundaries] = useState(false);
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();

  const isBoundaryTerritoryNonEmpty = useCallback((territory: BoundaryTerritory) =>
    !isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
      props.boundaryTerritoryGroup.boundaryTerritoryGroupId,
      territory),
  [boundaryTerritoryAssignments, props.boundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const setIsFormValidRef = useRef(props.setIsFormValid);

  const {
    limit: boundarySettingListCountLimit,
    increaseLimit: increaseBoundarySettingListCountLimit,
  } = useLoadMoreListing(BoundaryTerritoryListingLimit);

  const boundaryTerritories = props.boundaryTerritoryGroup.settings.boundaryTerritories;
  const lineWidth = props.boundaryTerritoryGroup.settings.style.lineWidth;
  const lineColor = createColor(props.boundaryTerritoryGroup.settings.style.lineColor);
  const nonEmptyBoundaryTerritories = useMemo(() => boundaryTerritories.filter(isBoundaryTerritoryNonEmpty),
    [boundaryTerritories, isBoundaryTerritoryNonEmpty]);
  const boundaryTerritoriesToShow = showEmptyBoundaries ? boundaryTerritories : nonEmptyBoundaryTerritories;
  const hasEmptyBoundaryTerritories = boundaryTerritories.length > nonEmptyBoundaryTerritories.length;
  const isManualFill = props.boundaryTerritoryGroup.settings.boundaryTerritoryType === BoundaryTerritoryType.Manual;

  const handleChangeTerritoryColor = useCallback((id: BoundaryTerritoryId) => (color: ColorResult) => {
    const onChange = props.onBoundaryTerritoryGroupChange;
    onChange({
      ...props.boundaryTerritoryGroup,
      settings: {
        ...props.boundaryTerritoryGroup.settings,
        boundaryTerritories: boundaryTerritories.map((bt) => {
          if (id !== bt.boundaryTerritoryId) {
            return bt;
          }
          else {
            return {
              ...bt,
              style: {
                ...bt.style,
                color: guaranteeHash(color.hex),
                opacity: color.rgba.a * 100,
              },
            };
          }
        }),
      },
    });
  }, [boundaryTerritories, props.boundaryTerritoryGroup, props.onBoundaryTerritoryGroupChange]);

  const handleChangeTerritoryName = useCallback((id: string, name: string) => {
    const onChange = props.onBoundaryTerritoryGroupChange;
    onChange({
      ...props.boundaryTerritoryGroup,
      settings: {
        ...props.boundaryTerritoryGroup.settings,
        boundaryTerritories: props.boundaryTerritoryGroup.settings.boundaryTerritories.map(
          bt => bt.boundaryTerritoryId === id ?
            {
              ...bt,
              displayName: name,
              translate: false,
            } : bt),
      },
    });
  }, [props.boundaryTerritoryGroup, props.onBoundaryTerritoryGroupChange]);

  useEffect(() => {
    for (const boundaryTerritory of boundaryTerritories) {
      if (isTextEmpty(boundaryTerritory.displayName)) {
        setIsFormValidRef.current(false);
        return;
      }
    }

    setIsFormValidRef.current(true);
  }, [boundaryTerritories]);

  return (
    <div>
      {!isManualFill || hasEmptyBoundaryTerritories && (
        <div css={boundarySettingsActionsWrapperStyle}>
          {!isManualFill && (
            <BoundarySettingsRestoreButtonComponent
              css={restoreColorsButtonStyle}
              prefixIcon={faSync}
              text={t('Restore Default Colors')}
              onClick={props.restoreDefaultColors}
            />
          )}
          {hasEmptyBoundaryTerritories && (
            <label css={toggleLabelStyle}>
              {t('Show empty groups')}
              <ToggleComponent
                isOn={showEmptyBoundaries}
                onChange={setShowEmptyBoundaries}
              />
            </label>
          )}
        </div>
      )}

      {(boundaryTerritoriesToShow.length > 0) && (
        <BoundarySettingsTableComponent
          css={addTopsideDelimiterStyle}
          dataHeadingLabel={t('Group Names')}
          dataRows={(
            <>
              {boundaryTerritoriesToShow.map((item, index) => {
                if (index >= boundarySettingListCountLimit) {
                  return null;
                }

                const color = createColorWithOpacity(
                  item.style.color,
                  item.style.opacity / 100,
                );

                return (
                  <BoundarySettingsTableRowComponent
                    key={item.boundaryTerritoryId}
                    index={index + 1}
                    data={item.custom ? (
                      <BoundarySettingsCustomTerritoryDataComponent
                        territory={item}
                        onTerritoryNameChange={handleChangeTerritoryName}
                      />
                    ) : (
                      <LimitNumberOfLinesComponent maxNumberOfLines={2} >
                        {translateBoundaryTerritoryDisplayName(item, t)}
                      </LimitNumberOfLinesComponent>
                    )}
                    lineColor={lineColor}
                    lineWidth={lineWidth}
                    color={color}
                    onColorChange={handleChangeTerritoryColor(item.boundaryTerritoryId)}
                  />
                );
              })}
              {(boundaryTerritoriesToShow.length > boundarySettingListCountLimit) && (
                <BoundarySettingsLoadMoreRowComponent onClick={increaseBoundarySettingListCountLimit} />
              )}
            </>
          )}
        />
      )}
    </div>
  );
};
